import { createApp } from 'vue';
import { createI18n } from 'vue-i18n';

import enGeneralSettings from 'dashboard/i18n/locale/en/generalSettings.json';
import enLogin from 'dashboard/i18n/locale/en/login.json';
import enResetPassword from 'dashboard/i18n/locale/en/resetPassword.json';
import enSetNewPassword from 'dashboard/i18n/locale/en/setNewPassword.json';
import enSettings from 'dashboard/i18n/locale/en/settings.json';
import enSignup from 'dashboard/i18n/locale/en/signup.json';
import * as Sentry from '@sentry/vue';
import { ANALYTICS_IDENTITY } from 'dashboard/constants/appEvents';
import AnalyticsHelper from 'dashboard/helper/AnalyticsHelper';
import { emitter } from 'shared/helpers/mitt';
import App from '../v3/App.vue';
import router, { initalizeRouter } from '../v3/views/index';
import store from '../v3/store';
// import { emitter } from '../shared/helpers/mitt';

// Commenting it out for Vite migration
// Vue.config.env = process.env;

const en = {
  ...enGeneralSettings,
  ...enLogin,
  ...enResetPassword,
  ...enSetNewPassword,
  ...enSettings,
  ...enSignup,
};

const localeLoaders = import.meta.glob([
  '../dashboard/i18n/locale/*/generalSettings.json',
  '../dashboard/i18n/locale/*/login.json',
  '../dashboard/i18n/locale/*/resetPassword.json',
  '../dashboard/i18n/locale/*/setNewPassword.json',
  '../dashboard/i18n/locale/*/settings.json',
  '../dashboard/i18n/locale/*/signup.json',
]);

const loadLocaleMessages = async locale => {
  if (!locale || locale === 'en') return en;

  const localePrefix = `../dashboard/i18n/locale/${locale}/`;
  const loaders = Object.entries(localeLoaders)
    .filter(([path]) => path.startsWith(localePrefix))
    .map(([, loader]) => loader());

  if (!loaders.length) return en;

  const localeModules = await Promise.all(loaders);
  return Object.assign({}, en, ...localeModules.map(module => module.default));
};

const bootstrap = async () => {
  const selectedLocale = window.waselConfig.selectedLocale || 'en';
  const selectedMessages = await loadLocaleMessages(selectedLocale);
  const messages = { en };

  if (selectedLocale !== 'en') {
    messages[selectedLocale] = selectedMessages;
  }

  const i18n = createI18n({
    legacy: false, // https://github.com/intlify/vue-i18n/issues/1902
    locale: selectedLocale,
    fallbackLocale: 'en',
    messages,
  });

  const app = createApp(App);
  app.use(i18n);
  app.use(store);
  app.use(router);

  if (window.errorLoggingConfig) {
    Sentry.init({
      app,
      dsn: window.errorLoggingConfig,
      denyUrls: [
        // Chrome extensions
        /^chrome:\/\//i,
        /chrome-extension:/i,
        /extensions\//i,

        // Locally saved copies
        /file:\/\//i,

        // Safari extensions.
        /safari-web-extension:/i,
        /safari-extension:/i,
      ],
      integrations: [Sentry.browserTracingIntegration({ router })],
      ignoreErrors: [
        'ResizeObserver loop completed with undelivered notifications',
      ],
    });
  }

  AnalyticsHelper.init();
  emitter.on(ANALYTICS_IDENTITY, ({ user }) => {
    AnalyticsHelper.identify(user);
  });
  initalizeRouter();

  // ES modules run after the document is parsed, so waiting for every image,
  // font and stylesheet delays the login form without making mounting safer.
  app.mount('#app');
};

bootstrap();
